/**
 * Pure pixel/percent placement of one event block inside a day column: the
 * vertical axis from calendarDayGeometry.ts's ratios, the horizontal axis
 * from calendarOverlap.ts's lane. Shared by CalendarDayTimeline and
 * CalendarWeekDayColumn so both views agree on gaps and gutters.
 */
import type { EventGeometry } from './calendarDayGeometry';

/** Vertical pixels kept clear below a block so back-to-back events never touch. */
export const EVENT_BLOCK_GAP = 2;
/** Horizontal gutter between side-by-side lanes, as a percent of the column. */
export const LANE_GUTTER_PERCENT = 2;

export interface EventBlockLayout {
  top: number;
  height: number;
  left: `${number}%`;
  width: `${number}%`;
}

function roundPercent(value: number): number {
  return Math.round(value * 100) / 100;
}

export function getEventBlockLayout(
  geometry: EventGeometry,
  lane: number,
  laneCount: number,
  gridHeight: number,
): EventBlockLayout {
  const top = Math.round(geometry.topRatio * gridHeight);
  const bottom = Math.round((geometry.topRatio + geometry.heightRatio) * gridHeight);
  const height = Math.max(bottom - top - EVENT_BLOCK_GAP, 1);

  const widthPercent = roundPercent((100 - LANE_GUTTER_PERCENT * (laneCount - 1)) / laneCount);
  const leftPercent = roundPercent(lane * (widthPercent + LANE_GUTTER_PERCENT));

  return {
    top,
    height,
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
  };
}
